import { Tooltip as ReactTooltip } from 'react-tooltip';
import historyIcon from '/asset/history.svg';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { logout } from '../store/authSlice';
import LogoutIcon from '/asset/logout.svg';
import { useEffect } from 'react';
import userIcon from '/asset/user.svg';
import { fetchPurchaseCart } from '../store/purchaseSlice';
import CartBasketTop from './CartBasketTop';
import Content from './Content';
import { setLoginFrom } from '../store/setupSlice';

const Header: React.FC<{
  logoOnly?: boolean;
  hideActions?: boolean;
  loginFrom?: 'bnplcheckout' | 'checkout' | 'profile' | 'storefront' | 'other';
}> = ({ logoOnly = false, hideActions = false, loginFrom = 'other' }) => {
  const auth_token = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { routesPrefix } = useSelector((state: RootState) => state.setup);
  const storeInfo = useSelector((state: RootState) => state.products.storeInfo);
  const parseTimeToMinutes = (timeValue?: string | null): number | null => {
    if (!timeValue) {
      return null;
    }

    const timePart = timeValue.includes('T')
      ? timeValue.split('T')[1]?.slice(0, 8)
      : timeValue.trim();

    if (!timePart) {
      return null;
    }

    const normalized = timePart.toLowerCase();
    const amPmMatch = normalized.match(/^(\d{1,2}):(\d{2})(?:\s*(am|pm))$/);

    if (amPmMatch) {
      let hours = Number(amPmMatch[1]);
      const minutes = Number(amPmMatch[2]);
      const period = amPmMatch[3];

      if (period === 'pm' && hours < 12) {
        hours += 12;
      }

      if (period === 'am' && hours === 12) {
        hours = 0;
      }

      return hours * 60 + minutes;
    }

    const twentyFourHourMatch = normalized.match(
      /^(\d{1,2}):(\d{2})(?::\d{2})?$/
    );

    if (!twentyFourHourMatch) {
      return null;
    }

    const hours = Number(twentyFourHourMatch[1]);
    const minutes = Number(twentyFourHourMatch[2]);

    if (hours > 23 || minutes > 59) {
      return null;
    }

    return hours * 60 + minutes;
  };

  const formatMinutesToAmPm = (minutesValue: number | null): string => {
    if (minutesValue === null) {
      return '';
    }

    const hours24 = Math.floor(minutesValue / 60);
    const minutes = minutesValue % 60;
    const period = hours24 >= 12 ? 'pm' : 'am';
    const hours12 = hours24 % 12 || 12;

    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const convertUtcMinutesToLocalMinutes = (
    utcMinutesValue: number | null
  ): number | null => {
    if (utcMinutesValue === null) {
      return null;
    }

    const utcHours = Math.floor(utcMinutesValue / 60);
    const utcMinutes = utcMinutesValue % 60;
    const nowUtc = new Date();
    const asUtcDate = new Date(
      Date.UTC(
        nowUtc.getUTCFullYear(),
        nowUtc.getUTCMonth(),
        nowUtc.getUTCDate(),
        utcHours,
        utcMinutes
      )
    );

    return asUtcDate.getHours() * 60 + asUtcDate.getMinutes();
  };

  const openingMinutes = convertUtcMinutesToLocalMinutes(
    parseTimeToMinutes(storeInfo?.openingTime)
  );
  const closingMinutes = convertUtcMinutesToLocalMinutes(
    parseTimeToMinutes(storeInfo?.closingTime)
  );
  const formattedOpeningTime = formatMinutesToAmPm(openingMinutes);
  const formattedClosingTime = formatMinutesToAmPm(closingMinutes);

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const isStoreOpenNow =
    openingMinutes !== null &&
    closingMinutes !== null &&
    (openingMinutes <= closingMinutes
      ? currentMinutes >= openingMinutes && currentMinutes <= closingMinutes
      : currentMinutes >= openingMinutes || currentMinutes <= closingMinutes);

  useEffect(() => {
    dispatch(fetchPurchaseCart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth_token]);

  return (
    <div className="w-full">
      <ReactTooltip id="logout-tooltip" place="bottom" content="Logout" />
      <ReactTooltip id="history-tooltip" place="bottom" content="History" />
      <ReactTooltip id="cart-tooltip" place="bottom" content="Cart" />
      <ReactTooltip id="account-tooltip" place="bottom" content="Account" />

      <div
        className="md:px-[50px] bg-transparent
          pb-[30px] max-w-[1350px] m-auto
          md:flex md:flex-row-reverse
          md:align-center md:justify-between 
          md:gap-[20px] md:items-center 
          "
      >
        {!storeInfo?.isStorefrontLoginDisabled && !hideActions && (
          <div
            className="
              bg-secondary-500
              md:bg-transparent
              md:mt-[5px]
              flex flex-row items-center
              w-full justify-between
              md:w-auto md:justify-center
              lg:mx-0
              gap-[20px]
              border-b-[1px]
              border-secondary-100
              md:border-none
              shadow-md
              md:shadow-none
              py-[10px]
              px-[20px]
              "
          >
            {/* Left: user/account icon — visible on mobile only */}
            <div className="md:hidden">
              {auth_token === null ? (
                <div
                  className="w-[22px] rounded-md h-[22px]
                      text-black text-[13px] justify-center
                      flex font-bold cursor-pointer mt-[1px]
                      font-dmsans"
                  onClick={() => {
                    dispatch(setLoginFrom(loginFrom));
                    navigate(`${routesPrefix}auth`);
                  }}
                >
                  <img
                    src={userIcon}
                    className="w-full h-full object-cover invert md:invert-0"
                  />
                </div>
              ) : (
                <div
                  data-tooltip-id="account-tooltip"
                  className="w-[22px] rounded-md h-[22px]
                      text-black text-[13px] justify-center
                      flex font-bold cursor-pointer mt-[1px]
                      font-dmsans"
                  onClick={() => navigate(`${routesPrefix}account`)}
                >
                  <img
                    src={userIcon}
                    className="w-[22px] h-[22px] object-cover invert md:invert-0"
                  />
                </div>
              )}
            </div>

            {/* Right: cart + other icons (md+: includes user icon in this group) */}
            <div
              className="flex items-center
              max-w-[100px] md:max-w-none"
            >
              {auth_token === null && (
                <div
                  className="hidden md:flex w-[50px] h-[22px]
                      text-black text-[13px] justify-center
                      font-bold cursor-pointer mt-[1px]
                      font-dmsans mr-[10px]"
                  onClick={() => {
                    dispatch(setLoginFrom(loginFrom));
                    navigate(`${routesPrefix}auth`);
                  }}
                >
                  <img
                    src={userIcon}
                    className="h-full object-cover invert-0"
                  />
                </div>
              )}
              <CartBasketTop isAtBottom={false} />
              {auth_token !== null && (
                <>
                  <div className="flex gap-[10px] flex-1">
                    <div
                      data-tooltip-id="history-tooltip"
                      className="w-[22px] rounded-md h-[22px]
                          text-black text-[13px] justify-center
                          flex font-bold cursor-pointer mt-[1px]
                          font-dmsans"
                      onClick={() => navigate(`${routesPrefix}history`)}
                    >
                      <img
                        src={historyIcon}
                        className="w-[22px] h-[22px] object-cover invert md:invert-0"
                      />
                    </div>

                    <div
                      data-tooltip-id="account-tooltip"
                      className="hidden md:flex w-[22px] rounded-md h-[22px]
                          text-black text-[13px] justify-center
                          font-bold cursor-pointer mt-[1px]
                          font-dmsans"
                      onClick={() => navigate(`${routesPrefix}account`)}
                    >
                      <img
                        src={userIcon}
                        className="w-[22px] h-[22px] object-cover invert-0"
                      />
                    </div>
                  </div>
                  <div
                    className="w-[48px] rounded-md h-[22px]
                      text-black text-[13px] justify-center
                      flex font-bold cursor-pointer mt-[1px]
                      font-dmsans
                      ml-[12px]"
                    onClick={() => {
                      dispatch(logout());
                      navigate(`${routesPrefix}`);
                    }}
                  >
                    <img
                      src={LogoutIcon}
                      className="w-[22px] h-[22px] object-contain invert md:invert-0"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        <Content>
          <div
            className={`w-full m-auto pt-[20px] min-h-[50px]  
              justify-center md:justify-between gap-[20px] 
              ${logoOnly ? 'flex flex-col' : 'flex flex-col md:flex-row'}
              `}
          >
            {storeInfo?.coverImage && (
              <div
                className={`w-full gap-[20px]
                    cursor-pointer 
                    flex items-center content-center
                ${logoOnly ? 'm-auto' : ''}
                `}
                onClick={() => navigate(`${routesPrefix}`)}
              >
                <div>
                  <img
                    src={storeInfo?.coverImage}
                    className="mb-3 md:mb-0 
                      h-[80px] object-contain"
                    alt={storeInfo?.name}
                  />
                </div>
                <div className="text-left">
                  <div
                    className="w-full py-1
                    text-left 
                    content-center font-bold 
                    text-primary-800"
                  >
                    {storeInfo?.name}
                  </div>
                  <div>
                    <div
                      className="text-gray-500 flex
                        items-center gap-[5px]
                        justify-start
                        text-[12px] font-medium"
                    >
                      {storeInfo?.address}
                    </div>
                    {storeInfo?.phoneNumber && (
                      <div
                        className="text-gray-500 flex
                          items-center gap-[5px]
                          justify-start mt-[5px]
                          text-[12px] font-medium"
                      >
                        {storeInfo.phoneNumber}
                      </div>
                    )}
                    <div
                      className="text-gray-500 flex
                        items-center gap-[5px]
                        justify-start mt-[5px]
                        text-[12px] font-medium"
                    >
                      <div
                        className={`font-medium
                        rounded-md p-1 text-[10px]
                        flex gap-[5px] px-3 shadow-sm
                        ${
                          isStoreOpenNow
                            ? 'bg-secondary-500 text-white'
                            : 'bg-red-500 text-white'
                        }
                        `}
                      >
                        <img
                          src="/asset/mdi_clock-outline.svg"
                          className="w-[14px]"
                        />

                        {isStoreOpenNow ? 'Open' : 'Closed'}
                      </div>
                      {formattedOpeningTime && formattedClosingTime && (
                        <div
                          className="
                          pl-[10px] leading-[30px] "
                        >
                          {formattedOpeningTime} - {formattedClosingTime}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Content>
      </div>
    </div>
  );
};

export default Header;
